/**
 * How numbers read on screen and in the packet (docs/CONTENT_STANDARDS.md §6, risk-model §7):
 * natural frequencies rounded to whole numbers up to 10 and to the nearest 5 above; days on the
 * target ladder in the unit a person would use; whole dollars with bands as "$30–45".
 */
import type { DataConfidence, IsoDate } from '../engine/types';

// ---------------------------------------------------------------------------------------------
// Natural frequencies
// ---------------------------------------------------------------------------------------------

export type Per100 =
  | { kind: 'fewer' }
  | { kind: 'number'; n: number }
  | { kind: 'almost_all' };

/** A probability as households out of 100: under 1, 1–10 whole, above 10 to the nearest 5. */
export function per100(p: number): Per100 {
  const n = 100 * Math.min(1, Math.max(0, p));
  if (n < 1) return { kind: 'fewer' };
  if (n >= 97.5) return { kind: 'almost_all' };
  if (n <= 10) return { kind: 'number', n: Math.max(1, Math.round(n)) };
  return { kind: 'number', n: Math.round(n / 5) * 5 };
}

function per100Text(v: Per100): string {
  if (v.kind === 'fewer') return 'fewer than 1';
  if (v.kind === 'almost_all') return 'almost all';
  return String(v.n);
}

/** The bracketed range after a natural frequency, or '' when it adds nothing. */
function rangeText(main: Per100, lo?: number, hi?: number): string {
  if (lo === undefined || hi === undefined) return '';
  const l = per100(lo);
  const h = per100(hi);
  const lt = per100Text(l);
  const ht = per100Text(h);
  if (lt === ht || (lt === per100Text(main) && ht === per100Text(main))) return '';
  if (l.kind === 'fewer' && h.kind === 'almost_all') return '';
  if (l.kind === 'fewer') return ` (up to ${ht})`;
  if (h.kind === 'almost_all') return ` (${lt} or more)`;
  return ` (${lt}–${ht})`;
}

/** "in the next 10 years" / "in the next year". */
export function inYears(years: number): string {
  return years === 1 ? 'in the next year' : `in the next ${years} years`;
}

/** The chance of at least one event in `years` years at `ratePerYear`. */
export function chanceWithin(ratePerYear: number, years: number): number {
  return 1 - Math.exp(-ratePerYear * years);
}

/**
 * "About 25 (15–40) of 100 households like yours will lose power in a windstorm in the next 10 years."
 * `p`, `lo` and `hi` are chances over the whole horizon.
 */
export function frequencySentence(p: number, what: string, years: number, lo?: number, hi?: number): string {
  const main = per100(p);
  const tail = `will ${what} ${inYears(years)}.`;
  if (main.kind === 'almost_all') return `Almost all households like yours ${tail}`;
  if (main.kind === 'fewer') return `Fewer than 1 in 100 households like yours ${tail}`;
  return `About ${main.n}${rangeText(main, lo, hi)} of 100 households like yours ${tail}`;
}

/** "about 10 of 100", for short labels. */
export function naturalFrequency(p: number): string {
  const v = per100(p);
  if (v.kind === 'almost_all') return 'almost all';
  if (v.kind === 'fewer') return 'fewer than 1 of 100';
  return `about ${v.n} of 100`;
}

// ---------------------------------------------------------------------------------------------
// Short chances for tables, worded exactly as the engine's sentences word them
// (crates/rr-hazards/src/sentence.rs), so a table and the card beside it never disagree: two
// significant figures, whole numbers from 1 to 10, "in 1,000" below 1 in 100, "1 in N" below 1 in
// 1,000, "nearly every household" from 99.5 in 100.
// ---------------------------------------------------------------------------------------------

/** `x > 0` to two significant figures (0 for anything else). */
export function roundSig2(x: number): number {
  if (!(Number.isFinite(x) && x > 0)) return 0;
  let e = Math.floor(Math.log10(x));
  // Guard against log10 rounding just across a power of ten.
  if (10 ** e > x) e -= 1;
  else if (10 ** (e + 1) <= x) e += 1;
  const scale = 10 ** (e - 1);
  return Math.round(x / scale) * scale;
}

/** A whole number with thousands separators: 12500 -> "12,500". */
export function thousands(n: number): string {
  return usd0.format(Math.round(n));
}

/** At most two significant figures without trailing zeros: "4.5", "81", "2,500". */
export function sig2(x: number): string {
  const r = roundSig2(x);
  if (r >= 10) return thousands(r);
  return String(Number(r.toPrecision(2)));
}

function per100Word(n: number): string {
  if (n >= 99.5) return 'nearly all';
  if (n < 0.5) return 'fewer than 1';
  if (n < 10) return String(Math.round(n));
  return thousands(roundSig2(n));
}

function per1000Word(n: number): string {
  if (n < 0.5) return 'fewer than 1';
  if (n < 10) return String(Math.round(n));
  return thousands(roundSig2(n));
}

/** "1 in 2,500" for a small chance `p` (below 1 in 10 or so). */
export function oneIn(p: number): string {
  if (p < 1e-6) return 'fewer than 1 in 1,000,000';
  const n = 1 / p;
  return `1 in ${n < 10 ? Math.max(2, Math.round(n)) : thousands(roundSig2(n))}`;
}

/** The engine's bracketed range: "(63–98)" when both ends are numbers, "(44 to nearly all)" otherwise. */
function bracket(lo: string, hi: string): string {
  if (lo === hi) return '';
  const numeric = (s: string) => /^[\d,]+$/.test(s);
  return numeric(lo) && numeric(hi) ? ` (${lo}–${hi})` : ` (${lo} to ${hi})`;
}

/**
 * A chance over the whole horizon for a table cell: "about 86 of 100", "about 9 in 1,000",
 * "about 1 in 2,000", "nearly every household". With `lo` and `hi` (expert estimates) the range
 * follows the number the way the engine's sentences put it: "about 86 (63–98) of 100".
 */
export function chanceShort(p: number, lo?: number, hi?: number): string {
  const withRange = lo !== undefined && hi !== undefined;
  if (p * 100 >= 99.5) return 'nearly every household';
  if (p * 100 >= 0.95) {
    const r = withRange ? bracket(per100Word(lo * 100), per100Word(hi * 100)) : '';
    return `about ${per100Word(p * 100)}${r} of 100`;
  }
  if (p * 1000 >= 0.95) {
    const r = withRange ? bracket(per1000Word(lo * 1000), per1000Word(hi * 1000)) : '';
    return `about ${per1000Word(p * 1000)}${r} in 1,000`;
  }
  if (p >= 1e-5) {
    const r = withRange ? bracket(oneIn(lo), oneIn(hi)) : '';
    return `about ${oneIn(p)}${r}`;
  }
  return 'fewer than 1 in 100,000';
}

/**
 * How often in a year, for the second line under a chance: "about 4.5 times a year", "about once a
 * year" (never "about 1 times a year"), otherwise the chance in any one year, "about 1 in 190 a
 * year", the way the dial's "1-in-100" is read.
 */
export function perYearWords(ratePerYear: number, annualProbability: number): string {
  if (ratePerYear >= 1.05) return `about ${sig2(ratePerYear)} times a year`;
  if (ratePerYear >= 0.75) return 'about once a year';
  if (!(annualProbability > 0)) return 'no known chance a year';
  return `about ${oneIn(annualProbability)} a year`;
}

/**
 * A rare catastrophe's chance as a range only, never a point (CONTENT_STANDARDS §6: expert numbers
 * show as ranges): "between 1 in 200 and 1 in 41" over `years`, or, when the range spans more than
 * 1,000 times, "very unlikely: less than 1 in 100".
 */
export function rangeOnly(rateLow: number, rateHigh: number, years: number): string {
  const lo = chanceWithin(Math.max(0, rateLow), years);
  const hi = chanceWithin(Math.max(0, rateHigh), years);
  if (!(hi > 0)) return 'no known chance';
  if (!(rateLow > 0) || rateHigh / rateLow > 1000 || lo < 1e-6) return `very unlikely: less than ${oneIn(hi)}`;
  const a = oneIn(lo);
  const b = oneIn(hi);
  return a === b ? `about ${a}` : `between ${a} and ${b}`;
}

/**
 * A chance cut into pieces for display, so that "1 in 2,000" and a range like "(63–98)" can be kept
 * on one line: `kind` is 'one_in' or 'range' for those pieces and '' for the rest.
 */
export function chancePieces(s: string): { text: string; kind: '' | 'one_in' | 'range' }[] {
  const out: { text: string; kind: '' | 'one_in' | 'range' }[] = [];
  let last = 0;
  for (const m of s.matchAll(/1 in [\d,]+|\(\d[\d,]*–\d[\d,]*\)/g)) {
    if (m.index > last) out.push({ text: s.slice(last, m.index), kind: '' });
    out.push({ text: m[0], kind: m[0].startsWith('(') ? 'range' : 'one_in' });
    last = m.index + m[0].length;
  }
  if (last < s.length) out.push({ text: s.slice(last), kind: '' });
  return out;
}

/** The expert-view percentage: at most two significant figures. */
export function percent(p: number): string {
  const v = p * 100;
  if (v === 0) return '0%';
  if (v < 0.1) return 'under 0.1%';
  if (v >= 10) return `${Math.round(v)}%`;
  if (v >= 1) return `${Math.round(v * 10) / 10}%`;
  return `${Math.round(v * 100) / 100}%`;
}

// ---------------------------------------------------------------------------------------------
// Days
// ---------------------------------------------------------------------------------------------

interface DayUnit {
  unit: 'day' | 'week' | 'month' | 'year';
  n: number;
}

function unitOf(d: number): DayUnit {
  if (d === 365) return { unit: 'year', n: 1 };
  if (d === 14 || d === 21) return { unit: 'week', n: d / 7 };
  if (d === 45) return { unit: 'month', n: 1.5 };
  if (d >= 30 && d % 30 === 0) return { unit: 'month', n: d / 30 };
  if (d > 30 && d < 365) return { unit: 'month', n: Math.round(d / 30) };
  if (d > 365) return { unit: 'year', n: Math.round((d / 365) * 10) / 10 };
  return { unit: 'day', n: d };
}

function nText(n: number): string {
  if (n === 0.5) return '½';
  if (Number.isInteger(n)) return String(n);
  if (n % 1 === 0.5) return `${Math.floor(n)}½`;
  return String(Math.round(n * 10) / 10);
}

/** "half a day", "1 day", "3 days", "2 weeks", "1 month", "3 months", "1 year". */
export function dayPhrase(d: number): string {
  if (d === 0) return '0 days';
  if (d === 0.5) return 'half a day';
  const u = unitOf(d);
  return `${nText(u.n)} ${u.unit}${u.n === 1 ? '' : 's'}`;
}

/**
 * When help arrives or service is mostly back, in whole days: half a day under ¾ of a day, whole
 * days under two weeks as the packet has them (rr-plan `round_relief`), and longer spans in weeks,
 * months or years. The engine sends these unrounded, and "about 5.2 days" is not how anyone talks.
 */
export function reliefPhrase(d: number): string {
  if (!(d > 0)) return dayPhrase(0);
  if (d < 0.75) return dayPhrase(0.5);
  return dayPhrase(Math.max(1, Math.round(d)));
}

/** A target with its range: "about 3 days (2–5)", "about 2 weeks (10 days–3 weeks)". */
export function targetDays(value: number, low: number, high: number): string {
  const main = `about ${dayPhrase(value)}`;
  if (low === value && high === value) return main;
  if (low === value) return `${main} (up to ${dayPhrase(high)})`;
  const [ul, uv, uh] = [unitOf(low), unitOf(value), unitOf(high)];
  if (ul.unit === uv.unit && uv.unit === uh.unit && value !== 0.5 && low !== 0.5) {
    return `${main} (${nText(ul.n)}–${nText(uh.n)})`;
  }
  return `${main} (${dayPhrase(low)}–${dayPhrase(high)})`;
}

/** Months of income: "about 4 months (2–6)". */
export function targetMonths(value: number, low: number, high: number): string {
  const m = (v: number) => (v === 0.5 ? 'half a month' : `${nText(v)} month${v === 1 ? '' : 's'}`);
  if (low === value && high === value) return `about ${m(value)}`;
  return `about ${m(value)} (${nText(low)}–${nText(high)})`;
}

export function monthsPhrase(v: number): string {
  if (v === 0) return 'no months';
  if (v === 0.5) return 'half a month';
  return `${nText(v)} month${v === 1 ? '' : 's'}`;
}

/**
 * Notice time: "15 minutes to 12 hours", "1 minute to 3 days". Singular or plural follows the
 * number as shown, after rounding (0.02 hours is "1 minute", 1.04 hours is "1 hour").
 */
export function noticeRange(lowHours: number, highHours: number): string {
  const one = (h: number): string => {
    const m = Math.max(1, Math.round(h * 60));
    if (m < 60) return `${m} minute${m === 1 ? '' : 's'}`;
    if (h < 48) {
      const r = Math.round(h * 10) / 10;
      return `${nText(r)} hour${r === 1 ? '' : 's'}`;
    }
    const d = Math.round(h / 24);
    return `${d} day${d === 1 ? '' : 's'}`;
  };
  const low = one(lowHours);
  const high = one(highHours);
  return low === high ? low : `${low} to ${high}`;
}

// ---------------------------------------------------------------------------------------------
// Money and quantities
// ---------------------------------------------------------------------------------------------

const usd0 = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

/** Whole dollars: "$1,200". */
export function usd(n: number): string {
  return `$${usd0.format(Math.round(n))}`;
}

/** A price band: "$30–45"; "$30" when both ends match; "Free" at $0. */
export function band(low: number, high: number): string {
  const l = Math.round(low);
  const h = Math.round(high);
  if (l === 0 && h === 0) return 'Free';
  if (l === h) return usd(l);
  return `${usd(l)}–${usd0.format(h)}`;
}

// Catalogue and requirement units, pluralised as the packet does (rr-plan `packet/text.rs`,
// `plural` and `quantity`), so the screens and the printed packet say the same thing.
const IRREGULAR: Record<string, string> = {
  box: 'boxes',
  pouch: 'pouches',
  'person-day': 'person-days',
  'pet-day': 'pet-days',
  'person-month': 'person-months',
  'day per person': 'days per person',
  "day of one person's medicine": "days of one person's medicine",
  'pound of dry food': 'pounds of dry food',
  'ounce of powder': 'ounces of powder',
  "cycle's supply": "cycles' supplies",
  '8-ounce canister': '8-ounce canisters',
  '12-ounce bottle': '12-ounce bottles',
  '24-pack': '24-packs',
};
/** Units that never take an "s". */
const INVARIANT = new Set(['each', 'Wh', 'kcal', 'oz', 'fl oz', 'lb']);

export function plural(unit: string, qty: number): string {
  if (qty === 1 || INVARIANT.has(unit)) return unit;
  const irregular = IRREGULAR[unit];
  if (irregular) return irregular;
  // "gallon of tank space" -> "gallons of tank space": the head noun takes the plural.
  const of = unit.indexOf(' of ');
  if (of > 0) return `${plural(unit.slice(0, of), qty)}${unit.slice(of)}`;
  if (/[^aeiou]y$/.test(unit)) return `${unit.slice(0, -1)}ies`;
  if (/(s|x|ch|sh)$/.test(unit)) return `${unit}es`;
  return `${unit}s`;
}

/** A count for a quantity: whole numbers with separators ("1,200"), otherwise one decimal ("2.5"). */
function count(q: number): string {
  const r = Math.round(q * 10) / 10;
  return Number.isInteger(r) ? usd0.format(r) : r.toFixed(1);
}

/**
 * "12 gallons", "1 kit", "$100" for dollars, "16,000 kcal" for food counted in 2,000 kcal units,
 * "4 (one per person)" for items counted per person or pet, and "1 × 24-pack" for a unit that is
 * itself an amount.
 */
export function quantity(qty: number, unit: string): string {
  const kcal = /^([\d,]+) kcal$/.exec(unit);
  if (kcal) return `${count(qty * Number(kcal[1]!.replace(/,/g, '')))} kcal`;
  if (unit === 'dollar') return usd(qty);
  if (unit === 'each') return count(qty);
  if (unit === 'person' || unit === 'pet') return `${count(qty)} (one per ${unit})`;
  if (/^\d/.test(unit)) return `${count(qty)} × ${unit}`;
  return `${count(qty)} ${plural(unit, qty)}`;
}

// ---------------------------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------------------------

/** Severity as a word; the UI pairs it with a pattern so colour is never the only signal. */
export function severityBand(s: number): { level: 1 | 2 | 3 | 4 | 5; label: string } {
  if (s < 0.2) return { level: 1, label: 'Minor' };
  if (s < 0.4) return { level: 2, label: 'Moderate' };
  if (s < 0.6) return { level: 3, label: 'Serious' };
  if (s < 0.8) return { level: 4, label: 'Severe' };
  return { level: 5, label: 'Very severe' };
}

export const CONFIDENCE_LABELS: Record<DataConfidence, string> = {
  high: 'Based on data',
  medium: 'Mostly data',
  low: 'Rough data',
  prior: 'Expert estimate',
};

// ---------------------------------------------------------------------------------------------
// Dates (calendar dates in UTC, so a date never shifts with the viewer's time zone)
// ---------------------------------------------------------------------------------------------

const dateFmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
const monthFmt = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });

function parse(iso: IsoDate): Date {
  return new Date(`${iso}T00:00:00Z`);
}

export function isoDate(d: Date): IsoDate {
  return d.toISOString().slice(0, 10);
}

/** Today's calendar date where the viewer is. */
export function localToday(now = new Date()): IsoDate {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** "Oct 1, 2026". */
export function formatDate(iso: IsoDate): string {
  return dateFmt.format(parse(iso));
}

/** "October 2026". */
export function formatMonth(iso: IsoDate): string {
  return monthFmt.format(parse(iso));
}

/** The same day `months` later, clamped to the month's last day. */
export function addMonths(iso: IsoDate, months: number): IsoDate {
  const d = parse(iso);
  const day = d.getUTCDate();
  const target = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + months, 1));
  const last = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(day, last));
  return isoDate(target);
}

/** Whole months from `from` to `to` (negative when `to` is earlier). */
export function monthsBetween(from: IsoDate, to: IsoDate): number {
  const a = parse(from);
  const b = parse(to);
  let months = (b.getUTCFullYear() - a.getUTCFullYear()) * 12 + (b.getUTCMonth() - a.getUTCMonth());
  if (b.getUTCDate() < a.getUTCDate()) months -= 1;
  return months;
}

export function daysBetween(from: IsoDate, to: IsoDate): number {
  return Math.round((parse(to).getTime() - parse(from).getTime()) / 86_400_000);
}

// ---------------------------------------------------------------------------------------------
// Plan months, numbered as the packet numbers them
// ---------------------------------------------------------------------------------------------

/**
 * A plan month's name, numbered exactly as the printed packet numbers it (the packet is the
 * oracle): month 0 begins on the plan date, month 1 a month later, and so on (`PlanMonth.index`).
 * "Month 4 (February 2027)"; the month the household is in reads "This month (October 2026)", and
 * keeps its number when the plan began earlier ("This month: month 2 (December 2026)"), so a
 * purchase carries the same month on the screen as in the packet's checklists ("(month 4)").
 */
export function planMonthLabel(planningDate: IsoDate, index: number, currentMonth = 0): string {
  const when = formatMonth(addMonths(planningDate, index));
  if (index !== currentMonth) return `Month ${index} (${when})`;
  return index === 0 ? `This month (${when})` : `This month: month ${index} (${when})`;
}

/** A plan month inside a sentence, as the packet writes it: "month 12 (October 2027)". */
export function planMonthPhrase(planningDate: IsoDate, index: number): string {
  return `month ${index} (${formatMonth(addMonths(planningDate, index))})`;
}
